import BaseService from '../base-service.js';
import { processGeoJsonLayers } from '../utils/geojson-utils.js';

class EvacuationService extends BaseService {
  constructor() {
    super('evacuation-portal/');
  }

  // ==================== EVENTS ====================
  async getEvents(userId) {
    const params = new URLSearchParams({
      operation: 'get_events',
      userId: userId,
    });

    const response = await this.get(`events/?${params.toString()}`);

    return response;
  }

  async getActiveEvent(userId) {
    const events = await this.getEvents(userId);
    const activeEvent = events.events.find(
      event => event.Status?.toLowerCase() === 'active'
    );

    return activeEvent || null;
  }

  async getEvent(eventId) {
    return this.get(`events/${eventId}/`);
  }

  async createEvent(eventData) {
    return this.post('events/', eventData);
  }

  async endEvent(eventId, endData) {
    return this.post('events/', {
      operation: 'end_event',
      eventId: eventId,
      ...endData  // Spread officialFName, officialLName, comments
    });
  }

  async getLayers() {
    const params = new URLSearchParams({ operation: 'static_geojson' });
    const rawGeoJson = await this.get(`geojson/?${params.toString()}`);

    // Process and transform zones using shared utility
    return processGeoJsonLayers(rawGeoJson);
  }

  // ==================== ORDERS ====================
  async getOrders(userId, filters = {}) {
    const params = new URLSearchParams({
      operation: 'get_orders',
      userId: userId,
    });

    // Add optional eventId filter
    if (filters.eventId) {
      params.append('eventId', filters.eventId);
    }

    const response = await this.get(`orders/?${params.toString()}`);
    return response;
  }
  
  async createOrder(
    userId,
    eventId,
    zoneId,
    hasBaseParameters = false,
    percentageRemaining = 100,
    tcpEnabled = false,
    effectiveLanes = 1.0,
  ) {
    if (!zoneId) {
      throw new Error('Zone ID is required');
    }

    if (!eventId) {
      throw new Error('Event ID is required');
    }

    const response = await this.post('orders/', {
      operation: 'create_order',
      userId: userId,
      eventId: eventId,
      zoneId: zoneId,
      hasBaseParameters: hasBaseParameters ? 1 : 0,
      percentageRemaining: percentageRemaining,
      tcpEnabled: tcpEnabled ? 1 : 0,
      effectiveLanes: effectiveLanes,
    });

    return {
      addresses: response.addresses || [],
      order_details: response.order_details || null,
      calculations: response.calculations || {},
      evacuation_data: response.evacuation_data || {},
      order_geojson: response.order_geojson || null,
    };
  }

  async commitOrder(commitData) {
    if (!commitData.orderId) {
      throw new Error('Order ID is required');
    }

    return this.post('events/', {
      operation: 'commit_order',
      ...commitData, // Spread all fields
    });
  }

  async rescindOrder(rescindData) {
    if (!rescindData.orderId) {
      throw new Error('Order ID is required');
    }

    return this.post('events/', {
      operation: 'rescind_order',
      ...rescindData, // Spread all fields
    });
  }

  async getZoneSettings(eventId) {
    const params = new URLSearchParams({
      operation: 'get_zone_default_settings',
      eventId: eventId,
    });

    const response = await this.get(`orders/?${params.toString()}`);
    return response.zone_default_settings || [];
  }

  async getZonesForEvent(eventId) {
    if (!eventId) {
      throw new Error('Event ID is required to fetch zones');
    }

    const params = new URLSearchParams({
      operation: 'get_zones_by_event',
      eventId: eventId,
    });

    const response = await this.get(`orders/?${params.toString()}`);

    const zones = Array.isArray(response.zones)
      ? response.zones
      : response.zones
        ? [response.zones]
        : [];

    return zones;
  }
}

export default new EvacuationService();